import brand from '@/config/brand'

// ─── Built-in link definitions ────────────────────────────────────────────────
// These are the platform's core links. Visibility and labels are controlled per
// brand via brand.config.js → navigation.links. Nothing is hardcoded here.
const BUILT_IN_LINKS = [
  {
    key:         'home',
    name:        'navigation.home.title',
    route:       { name: 'shared.dashboard' },
    icon:        'solar:home-smile-linear',
    description: 'navigation.home.description',
    translate:   true,
    roles:       ['admin', 'exhibitor', 'buyer', 'speaker', 'sponsor', 'attendee', 'visitor'],
  },
  {
    key:         'my-eshow',
    name:        'navigation.my-eshow.title',
    route:       { name: 'shared.my-eshow' },
    icon:        'solar:monitor-smartphone-linear',
    description: 'navigation.my-eshow.description',
    translate:   true,
    roles:       ['admin', 'exhibitor', 'buyer', 'speaker', 'sponsor', 'attendee'],
  },
  {
    key:         'companies',
    name:        'navigation.companies.title',
    route:       { name: 'shared.companies' },
    icon:        'solar:buildings-2-linear',
    description: 'navigation.companies.description',
    translate:   true,
    roles:       ['admin', 'exhibitor', 'buyer', 'speaker', 'sponsor', 'attendee'],
  },
  {
    key:         'vib-buyers',
    name:        'navigation.vib-buyers.title',
    route:       { name: 'shared.vib-buyers' },
    icon:        'solar:bag-smile-linear',
    description: 'navigation.vib-buyers.description',
    translate:   true,
    roles:       ['admin', 'exhibitor', 'buyer', 'speaker', 'sponsor', 'attendee'],
  },
  {
    key:         'community',
    name:        'navigation.community.title',
    route:       { name: 'shared.community' },
    icon:        'solar:users-group-rounded-linear',
    description: 'navigation.community.description',
    translate:   true,
    roles:       ['admin', 'exhibitor', 'buyer', 'speaker', 'sponsor', 'attendee'],
  },
  {
    key:         'speakers',
    name:        'navigation.speakers.title',
    route:       { name: 'shared.speakers' },
    icon:        'solar:microphone-large-linear',
    description: 'navigation.speakers.description',
    translate:   true,
    roles:       ['admin', 'exhibitor', 'buyer', 'speaker', 'sponsor', 'attendee'],
  },
  {
    key:         'agenda',
    name:        'navigation.agenda.title',
    route:       { name: 'shared.agenda' },
    icon:        'solar:calendar-linear',
    description: 'navigation.agenda.description',
    translate:   true,
    roles:       ['admin', 'exhibitor', 'buyer', 'speaker', 'sponsor', 'attendee'],
  },
  {
    key:         'products',
    name:        'navigation.products.title',
    route:       { name: 'shared.products' },
    icon:        'solar:box-linear',
    description: 'navigation.products.description',
    translate:   true,
    roles:       ['admin', 'exhibitor', 'buyer', 'speaker', 'sponsor', 'attendee'],
  },
  {
    key:         'services',
    name:        'navigation.services.title',
    route:       { name: 'shared.services' },
    icon:        'solar:settings-linear',
    description: 'navigation.services.description',
    translate:   true,
    roles:       ['admin', 'exhibitor', 'buyer', 'speaker', 'sponsor', 'attendee'],
  },
  // {
  //   key:         'help-desk',
  //   name:        'navigation.help-desk.title',
  //   route:       { name: 'shared.help-desk' },
  //   icon:        'solar:help-linear',
  //   description: 'navigation.help-desk.description',
  //   translate:   true,
  //   roles:       ['admin', 'exhibitor', 'buyer', 'speaker', 'sponsor', 'attendee'],
  // },
  {
    key:         'foodshow-on-demand',
    name:        'navigation.foodshow-on-demand.title',
    route:       { name: 'shared.foodshow-on-demand' },
    icon:        'solar:clapperboard-play-linear',
    description: 'navigation.foodshow-on-demand.description',
    translate:   true,
    roles:       ['admin', 'exhibitor', 'buyer', 'speaker', 'sponsor', 'attendee', 'visitor'],
  },
]

// ─── Brand-aware link builder ─────────────────────────────────────────────────

function buildNavigationLinks() {
  const navConfig = brand.navigation || {}
  const linkOverrides = navConfig.links || {}
  const customLinks  = navConfig.custom || []

  // 1. Apply brand overrides to built-in links and filter disabled ones
  const builtIn = BUILT_IN_LINKS
    .filter(link => {
      const override = linkOverrides[link.key]
      // If the brand explicitly sets enabled: false, hide it.
      // If there is no entry for this key, default to showing it.
      return override ? override.enabled !== false : true
    })
    .map(link => {
      const override = linkOverrides[link.key] || {}
      return {
        ...link,
        // Label override: if a plain string is given, use it directly (no i18n lookup)
        ...(override.label ? { name: override.label, translate: false } : {}),
        // Icon override
        ...(override.icon  ? { icon: override.icon }  : {}),
        // Role override
        ...(override.roles ? { roles: override.roles } : {}),
      }
    })

  // 2. Map custom brand links into the same shape as built-in links
  const custom = customLinks.map(link => ({
    key:         link.key,
    name:        link.label,
    route:       link.url || link.route,
    icon:        link.icon || 'solar:link-linear',
    description: link.label,
    translate:   false,
    target:      link.target || '_self',
    roles:       link.roles || ['admin', 'exhibitor', 'buyer', 'speaker', 'sponsor', 'attendee'],
  }))

  return [...builtIn, ...custom]
}

// Exported list — computed once at module load time
export const navigationLinks = buildNavigationLinks()

// ─── Helper utilities ─────────────────────────────────────────────────────────

export const isExternalLink = (route) =>
  typeof route === 'string' && (route.startsWith('http://') || route.startsWith('https://'))

export const getNavigationDisplayName = (link, t = null) => {
  if (link.translate !== false && t && typeof t === 'function') {
    return t(link.name)
  }
  return link.name
}

export const getNavigationTarget = (link) => {
  if (link.target === '_blank') return '_blank'
  if (isExternalLink(link.route) && !link.target) return '_self'
  return null
}

export const getFilteredNavigationLinks = (userRole) => {
  if (!userRole) {
    const defaultKeys = ['home', 'my-eshow', 'community', 'help-desk']
    return navigationLinks.filter(link => defaultKeys.includes(link.key))
  }
  const role = userRole.toLowerCase()
  return navigationLinks.filter(link => link.roles.includes(role))
}
